import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    AccessibilityProps,
    Image,
    Platform,
    TouchableOpacity,
    View,
} from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { MoreInfoIcon, lockedContentRight } from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import {
    ASSET_CONTENT_MARKER_ID,
    ASSET_METADATA_ID,
    ASSET_PARENTAL_RATING,
    ASSET_SEASON_EPISODE_NUMBER_ID,
    ASSET_TITLE_ID,
    CHANNEL_LOGO_ID,
    MORE_INFO_BUTTON_ID,
} from 'config/testID';

import { MixedItem } from 'store/railsBuilder/reducer.types';
import { selectEntitlements } from 'store/user/selectors';

import { isAssetVod } from 'utils/checkVodType';
import isFunction from 'utils/function';
import getOrdinal from 'utils/getOrdinary';
import getVodStripesExpiryDate from 'utils/getVodStripesExpiryDate';
import useProfileParentalRating from 'utils/hooks/useProfileParentalRating';
import needsToLockContent from 'utils/parentalControl/needsToLockContent';
import { isWeb, landscapeAspectRatio } from 'utils/responsiveness';

import BaseTileImage from 'components/BaseTileImage';
import ContentMarkerIcons from 'components/ContentMarkerIcons';
import ActionButton from 'components/CustomButtons/ActionButton';
import Marker from 'components/Marker';
import { AspectRatio, ImageType } from 'components/NagraImage/types';
import { PLACEHOLDER_ICON_RIGHT } from 'components/Ribbon/constants';
import { OnUavAnalyticsProps, StandardUavProps } from 'components/Ribbon/types';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import useAccessibilityFocus from '../../hooks/useAccessibilityFocus';
import useGallery from '../../hooks/useGallery';
import ChannelLogo from '../ChannelLogo';
import Gradient from '../Gradient';
import styles from './styles';

export type Props = {
    item: MixedItem;
    focused: boolean;
    screenReaderEnabled: boolean;
    index: number;
    onPress(item: MixedItem, index: number): void;
    onShowPinDialog?(): void;
    onSetParentalControlCheckedCallback?(
        callback: undefined | (() => void),
    ): void;
    totalNumberOfItems: number;
    isWebEdgeCase?: boolean;
} & StandardUavProps &
    OnUavAnalyticsProps;

const {
    SCREEN_GALLERY_MORE_INFO_BUTTON,
    CARDS_TITLE_CONTENT_LOCKED,
    ADULT_LOCKED_CONTENT_DESCRIPTION,
    ACCESSIBILITY: {
        PLAYER: {
            ACCESSIBILITY_PLAYER_OPEN_DETAIL_PAGE,
            ACCESSIBILITY_PLAYER_OPEN_DETAIL_PAGE_WEB,
        },
        GALLERY: { ACCESSIBILITY_GALLERY_FIRST_ASSET },
    },
} = LANGUAGE_KEYS;

const TabletView = ({
    item,
    focused,
    screenReaderEnabled,
    onPress,
    onThirdPartyAppStart,
    onRailsSelection,
    onDeepLinkTriggered,
    railId,
    templateId,
    index,
    onShowPinDialog,
    onSetParentalControlCheckedCallback,
    totalNumberOfItems,
    isWebEdgeCase,
}: Props) => {
    const {
        id,
        title,
        channelId,
        contentType,
        startTime,
        endTime,
        vodProductIds,
        playbackType,
        vodStartTime,
    } = item;
    const {
        width,
        primary,
        isCatchupAsset,
        ableToStartOver,
        metadata,
        seriesMetadata,
        parentalRating,
        handlePress,
        isValidDeeplinkItem,
        shouldDisplaySubscribeIcon,
    } = useGallery({
        item,
        onPress,
        onThirdPartyAppStart,
        onRailsSelection,
        onDeepLinkTriggered,
        railId,
        templateId,
        index,
    });
    const { t } = useTranslation();
    const accessibleViewRef = useRef(null);

    const isVod = isAssetVod(playbackType);

    const profileParentalControlRating = useProfileParentalRating();
    const isContentLocked = needsToLockContent(
        profileParentalControlRating,
        parentalRating,
    );

    const entitlements = useSelector(selectEntitlements);

    const vodComingSoonDate = isVod && vodStartTime ? vodStartTime : undefined;

    const [isWebFocused, setIsWebFocused] = useState(focused);

    const onHandlePress = useCallback(() => {
        if (isContentLocked && isFunction(onShowPinDialog)) {
            if (isFunction(onSetParentalControlCheckedCallback)) {
                onSetParentalControlCheckedCallback(handlePress);
            }

            onShowPinDialog();
        } else {
            handlePress();
        }
    }, [
        isContentLocked,
        onShowPinDialog,
        handlePress,
        onSetParentalControlCheckedCallback,
    ]);

    const vodExpiryDate = useMemo(() => {
        if (isVod) {
            return getVodStripesExpiryDate(entitlements, vodProductIds);
        }

        return undefined;
    }, [entitlements, isVod, vodProductIds]);

    const accessibilityLabel = useMemo(() => {
        if (isContentLocked && index === 0) {
            return `${t(CARDS_TITLE_CONTENT_LOCKED)} ${t(
                ACCESSIBILITY_GALLERY_FIRST_ASSET,
                {
                    number: totalNumberOfItems,
                    interpolation: {
                        prefix: '%',
                        suffix: '%',
                        escapeValue: false,
                    },
                },
            )}`;
        }

        if (isContentLocked) {
            return `${t(CARDS_TITLE_CONTENT_LOCKED)} ${t(
                'ACCESSIBILITY_GALLERY_%NUMBER%_ASSET',
                {
                    number: getOrdinal(index + 1),
                    interpolation: {
                        prefix: '%',
                        suffix: '%',
                        escapeValue: false,
                    },
                },
            )}`;
        }

        if (index === 0) {
            return t(ACCESSIBILITY_GALLERY_FIRST_ASSET, {
                number: totalNumberOfItems,
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            });
        }

        return `${title} ${t('ACCESSIBILITY_GALLERY_%NUMBER%_ASSET', {
            number: getOrdinal(isWebEdgeCase ? (index % 2) + 1 : index + 1),
            interpolation: {
                prefix: '%',
                suffix: '%',
                escapeValue: false,
            },
        })}`;
    }, [title, totalNumberOfItems, index, isContentLocked]);

    const accessibleProps = useMemo(
        () => ({
            accessibilityLabel: `${accessibilityLabel} ${
                isWeb ? t(ACCESSIBILITY_PLAYER_OPEN_DETAIL_PAGE_WEB) : ''
            }`,
            accessibilityHint: Platform.select({
                default: t(ACCESSIBILITY_PLAYER_OPEN_DETAIL_PAGE),
                android: '',
            }),
            accessibilityElementsHidden: !focused,
            importantForAccessibility: focused
                ? 'auto'
                : ('no-hide-descendants' as AccessibilityProps['importantForAccessibility']),
            accessible: isWeb ? isWebFocused : focused,
            ref: accessibleViewRef,
            accessibilityActions: [{ name: 'activate' }],
            onAccessibilityAction: onHandlePress,
        }),
        [focused, isWebFocused, t, accessibilityLabel, onHandlePress],
    );

    const handleIntersect = useCallback((entries) => {
        entries.forEach((entry) => {
            setIsWebFocused(
                entry.isIntersecting && entry.boundingClientRect.top >= -100,
            );
        });
    }, []);

    const handleGalleryIntersectionObserver = useCallback(
        (element: Element) => {
            const options = {
                root: document,
                rootMargin: '0px',
            };

            const observer = new IntersectionObserver(handleIntersect, options);

            observer.observe(element);
        },
        [handleIntersect, focused],
    );

    useEffect(() => {
        if (isWeb) {
            const carouselItems = document.querySelectorAll(
                `[data-class="carousel_item_${item.id}"]`,
            );

            carouselItems.forEach(handleGalleryIntersectionObserver);
        }
    }, [handleGalleryIntersectionObserver, item.id]);

    useAccessibilityFocus({
        accessibleViewRef,
        focused: isWeb ? isWebFocused : focused,
    });

    return (
        <View
            style={styles.root}
            {...(Platform.OS === 'ios' && accessibleProps)}
            dataSet={{ class: focused ? `carousel_item_${item.id}` : '' }}
        >
            <TouchableOpacity
                // @ts-ignore
                accessible={Platform.OS === 'ios'}
                // @ts-ignore
                importantForAccessibility="no-hide-descendants"
                // @ts-ignore
                accessibilityElementsHidden={!isWeb}
                style={styles.imageContainer}
                activeOpacity={1}
                onPress={onHandlePress}
                role="button"
                {...(isWeb && focused
                    ? accessibleProps
                    : { 'aria-hidden': isWeb })}
                // @ts-ignore
                tabIndex={isWebFocused ? 0 : -1}
            >
                {isContentLocked ? (
                    <Image
                        accessible={false}
                        source={lockedContentRight}
                        style={styles.container}
                        resizeMode="cover"
                    />
                ) : (
                    <BaseTileImage
                        style={[
                            styles.coverImage,
                            {
                                width,
                                aspectRatio: landscapeAspectRatio,
                            },
                        ]}
                        placeHolder={PLACEHOLDER_ICON_RIGHT}
                        backupSourceResizeMode="contain"
                        resizeMode="cover"
                        contentId={id}
                        contentType={contentType}
                        imageType={ImageType.GALLERY}
                        aspect={AspectRatio.LANDSCAPE}
                        width={width}
                    />
                )}
            </TouchableOpacity>
            <Gradient />
            <View
                style={[
                    styles.contentContainer,
                    screenReaderEnabled && styles.accessibilityContainer,
                ]}
                {...(Platform.OS === 'android' && accessibleProps)}
                accessibilityElementsHidden
                aria-hidden={isWeb}
            >
                {channelId && (
                    <ChannelLogo
                        testID={CHANNEL_LOGO_ID}
                        channelId={channelId}
                    />
                )}
                <Text
                    testID={ASSET_TITLE_ID}
                    numberOfLines={2}
                    style={styles.title}
                    category={ETextCategory.title1}
                    color={ETextColor.neutral}
                >
                    {isContentLocked ? t(CARDS_TITLE_CONTENT_LOCKED) : title}
                </Text>
                {!isContentLocked && !!seriesMetadata && (
                    <Text
                        testID={ASSET_SEASON_EPISODE_NUMBER_ID}
                        numberOfLines={1}
                        style={styles.seriesMetadata}
                        category={ETextCategory.title3}
                        color={ETextColor.neutral}
                    >
                        {seriesMetadata}
                    </Text>
                )}
                <View style={styles.markerContainer}>
                    {((startTime && endTime) ||
                        vodExpiryDate ||
                        vodComingSoonDate) && (
                        <Marker
                            testID={ASSET_CONTENT_MARKER_ID}
                            startTime={startTime}
                            endTime={endTime}
                            bgColor={primary}
                            style={styles.marker}
                            expiryDate={vodExpiryDate}
                            comingSoonDate={vodComingSoonDate}
                        />
                    )}
                    <ContentMarkerIcons
                        parentalRatingTestID={ASSET_PARENTAL_RATING}
                        iconStyle={styles.markerIcons}
                        shouldDisplayCatchup={
                            isCatchupAsset || isValidDeeplinkItem
                        }
                        shouldDisplayStartOver={ableToStartOver}
                        shouldDisplaySubscribe={shouldDisplaySubscribeIcon}
                        parentalRating={parentalRating}
                        isContentLocked={isContentLocked}
                        tabIndex={-1}
                    />
                </View>
                {!!metadata && (
                    <Text
                        testID={ASSET_METADATA_ID}
                        style={styles.metadataText}
                        category={ETextCategory.body2}
                        color={ETextColor.light_grey}
                        accessibilityElementsHidden
                    >
                        {isContentLocked
                            ? t(ADULT_LOCKED_CONTENT_DESCRIPTION)
                            : metadata}
                    </Text>
                )}
                <ActionButton
                    accessible={false}
                    importantForAccessibility="no-hide-descendants"
                    accessibilityElementsHidden
                    testID={MORE_INFO_BUTTON_ID}
                    title={SCREEN_GALLERY_MORE_INFO_BUTTON}
                    onPress={onHandlePress}
                    fillColor={primary}
                    Icon={MoreInfoIcon}
                    buttonStyle={styles.button}
                    buttonTextStyle={styles.buttonText}
                    numberOfLines={1}
                    tabIndex={-1}
                />
            </View>
        </View>
    );
};

export default TabletView;
